import { AlertTriangle, CalendarDays, Clock, MapPin, Users } from 'lucide-react'
import type { ComponentType, ReactNode } from 'react'

import { cn } from '../cn'
import { Badge, type BadgeProps } from './badge'
import { Card } from './card'
import { EmptyState } from './empty-state'

export type EventTimelineStatusVariant = NonNullable<BadgeProps['variant']>

export type EventTimelineDetailTone = 'success' | 'warning' | 'destructive'

export interface EventTimelineItemDetail {
  icon?: ComponentType<{ className?: string }>
  label: ReactNode
  /** Colors the row (icon + text) to signal state; muted when absent. */
  tone?: EventTimelineDetailTone
}

export interface EventTimelineItem {
  id: string
  title: string
  subtitle?: ReactNode
  typeLabel?: string | null
  statusLabel?: string | null
  statusVariant?: EventTimelineStatusVariant
  start: Date | string
  end?: Date | string | null
  timezone?: string | null
  locationLabel?: string | null
  peopleLabel?: string | null
  warningLabel?: string | null
  href?: string | null
  external?: boolean
  ariaLabel?: string
  /** Renders the card as a button. Ignored when `href` is set. */
  onSelect?: () => void
  /** Replaces the built-in time/location/people rows entirely (e.g. date-only items). */
  details?: EventTimelineItemDetail[]
  /** Custom top-left slot content (e.g. a badge cluster). Overrides `typeLabel`. */
  leading?: ReactNode
}

export interface EventTimelineProps {
  items: EventTimelineItem[]
  empty?: ReactNode
  emptyTitle?: string
  emptyDescription?: ReactNode
  className?: string
  contentClassName?: string
}

export interface EventTimelinePanelProps {
  /** Filter pills / view switchers pinned above the scroll area. */
  toolbar?: ReactNode
  children: ReactNode
  /** Set the panel's height here (e.g. `h-[calc(100dvh-18rem)]`) — content scrolls inside. */
  className?: string
  contentClassName?: string
}

/**
 * The contained timeline shell from Road Warrior's Events page: a Card
 * with a pinned toolbar and an inner scroll area on a sunken background,
 * so the timeline cards pop against it.
 */
export function EventTimelinePanel({ toolbar, children, className, contentClassName }: EventTimelinePanelProps) {
  return (
    <Card className={cn('relative flex flex-col overflow-hidden shadow-md', className)}>
      {toolbar ? (
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border bg-muted/50 px-6 py-3">
          {toolbar}
        </div>
      ) : null}
      <div className={cn('flex-1 overflow-y-auto bg-muted/50 px-6 py-4', contentClassName)}>{children}</div>
    </Card>
  )
}

interface EventTimelineGroup {
  key: string
  label: string
  timezone?: string | null
  items: EventTimelineItem[]
}

export function EventTimeline({
  items,
  empty,
  emptyTitle = 'No Events',
  emptyDescription = 'Scheduled events will appear here.',
  className,
  contentClassName,
}: EventTimelineProps) {
  if (items.length === 0) {
    return (
      <div className={className}>
        {empty ?? (
          <EmptyState
            icon={<CalendarDays />}
            title={emptyTitle}
            description={emptyDescription}
            padding="md"
            className="min-h-56"
          />
        )}
      </div>
    )
  }

  const groups = groupTimelineItems(items)

  return (
    <div className={cn('space-y-6', className)}>
      {groups.map((group) => {
        const today = group.key === dateKey(new Date(), group.timezone)

        return (
          <section key={group.key} aria-label={group.label}>
            <div className="mb-3 flex items-center gap-2 px-1">
              <span
                className={cn('h-2.5 w-2.5 shrink-0 rounded-full', today ? 'bg-primary' : 'bg-muted-foreground/60')}
                aria-hidden="true"
              />
              <h3
                className={cn(
                  'text-xs font-semibold uppercase tracking-wider',
                  today ? 'text-primary' : 'text-muted-foreground',
                )}>
                {group.label}
              </h3>
            </div>
            <div
              className={cn(
                'ml-1 space-y-3 border-l-2 pl-5',
                today ? 'border-primary/20' : 'border-border',
                contentClassName,
              )}>
              {group.items.map((item) => (
                <EventTimelineCard key={item.id} item={item} />
              ))}
            </div>
          </section>
        )
      })}
    </div>
  )
}

function EventTimelineCard({ item }: { item: EventTimelineItem }) {
  const rows = item.details ?? builtInDetailRows(item)
  const asButton = !item.href && Boolean(item.onSelect)
  const Wrapper = item.href ? 'a' : asButton ? 'button' : 'div'

  return (
    <Wrapper
      {...(item.href
        ? {
            href: item.href,
            target: item.external ? '_blank' : undefined,
            rel: item.external ? 'noreferrer' : undefined,
            'aria-label': item.ariaLabel ?? item.title,
          }
        : asButton
          ? {
              type: 'button' as const,
              onClick: item.onSelect,
              'aria-label': item.ariaLabel ?? item.title,
            }
          : {})}
      className={cn(
        'block rounded-lg border bg-card p-4 text-card-foreground shadow-sm transition-shadow',
        (item.href || asButton) && 'hover:shadow-md',
        asButton && 'w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
      )}>
      {/* With no leading/type badge the title takes the header row itself —
          an empty left slot reads as a dead band above the card content. */}
      <div className="mb-2 flex min-w-0 items-center justify-between gap-2">
        {item.leading ? (
          <div className="flex min-w-0 flex-wrap items-center gap-1">{item.leading}</div>
        ) : item.typeLabel ? (
          <Badge variant="secondary" className="shrink-0 rounded px-2 py-0.5 text-[11px]">
            {item.typeLabel}
          </Badge>
        ) : (
          <p className="min-w-0 truncate text-sm font-semibold text-foreground">{item.title}</p>
        )}
        {item.statusLabel ? (
          <Badge variant={item.statusVariant ?? 'secondary'} className="shrink-0 rounded px-2 py-0.5 text-[11px]">
            {item.statusLabel}
          </Badge>
        ) : null}
      </div>

      {item.leading || item.typeLabel ? (
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-foreground">{item.title}</p>
          {item.subtitle ? <div className="mt-0.5 truncate text-xs text-muted-foreground">{item.subtitle}</div> : null}
        </div>
      ) : item.subtitle ? (
        <div className="min-w-0 truncate text-xs text-muted-foreground">{item.subtitle}</div>
      ) : null}

      {rows.length > 0 ? (
        <div className="mt-3 space-y-1.5 border-t border-border pt-3">
          {rows.map((row, index) => (
            <EventTimelineDetail key={index} icon={row.icon} label={row.label} tone={row.tone} />
          ))}
        </div>
      ) : null}

      {item.warningLabel && (
        <div className="mt-3 border-t border-border pt-3">
          <p className="flex min-w-0 items-center gap-1.5 text-xs font-medium text-warning-foreground">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{item.warningLabel}</span>
          </p>
        </div>
      )}
    </Wrapper>
  )
}

function builtInDetailRows(item: EventTimelineItem): EventTimelineItemDetail[] {
  const rows: EventTimelineItemDetail[] = []
  const timeLabel = formatTimeRange(item)
  if (timeLabel) {
    rows.push({ icon: Clock, label: timeLabel })
  }
  if (item.locationLabel) {
    rows.push({ icon: MapPin, label: item.locationLabel })
  }
  if (item.peopleLabel) {
    rows.push({ icon: Users, label: item.peopleLabel })
  }
  return rows
}

const DETAIL_TONE_CLASS: Record<EventTimelineDetailTone, string> = {
  success: 'font-medium text-success-foreground',
  warning: 'font-medium text-warning-foreground',
  destructive: 'font-medium text-destructive',
}

function EventTimelineDetail({ icon: Icon, label, tone }: EventTimelineItemDetail) {
  return (
    <p
      className={cn(
        'flex min-w-0 items-center gap-1.5 text-xs',
        tone ? DETAIL_TONE_CLASS[tone] : 'text-muted-foreground',
      )}>
      {Icon ? <Icon className={cn('h-3.5 w-3.5 shrink-0', !tone && 'text-muted-foreground/70')} /> : null}
      <span className="truncate">{label}</span>
    </p>
  )
}

function groupTimelineItems(items: EventTimelineItem[]): EventTimelineGroup[] {
  const groups = new Map<string, EventTimelineGroup>()

  for (const item of items) {
    const start = toValidDate(item.start)
    const key = start ? dateKey(start, item.timezone) : `undated-${item.id}`
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        label: start ? formatDateHeader(start, item.timezone) : 'DATE TBD',
        timezone: item.timezone,
        items: [],
      })
    }
    groups.get(key)!.items.push(item)
  }

  return Array.from(groups.values())
}

function toValidDate(value: Date | string | null | undefined): Date | null {
  if (!value) {
    return null
  }
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

function dateKey(date: Date, timezone?: string | null): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(timezone ? { timeZone: timezone } : {}),
  }).formatToParts(date)
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')}`
}

function formatDateHeader(date: Date, timezone?: string | null): string {
  return date
    .toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      ...(timezone ? { timeZone: timezone } : {}),
    })
    .toUpperCase()
    .replace(',', ' ·')
}

function formatTimeRange(item: EventTimelineItem): string | null {
  const start = toValidDate(item.start)
  if (!start) {
    return null
  }
  const end = toValidDate(item.end)
  const startLabel = formatTime(start, item.timezone, !end)
  if (!end) {
    return startLabel
  }
  return `${startLabel} - ${formatTime(end, item.timezone, true)}`
}

function formatTime(date: Date, timezone: string | null | undefined, includeTimezone: boolean): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    ...(timezone ? { timeZone: timezone } : {}),
    timeZoneName: includeTimezone && timezone ? 'short' : undefined,
  })
}
